import type { HelpAnchorId, WaitingOrder } from '@myrewards/contracts';
import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Text,
  type Selection,
} from 'react-aria-components';
import { Button, ButtonLink } from './Button';
import { Card } from './Card';
import type { CardHelpContent } from './CardHelp';
import { cx } from './cx';
import { HelpShownTag, useHelpAnchor } from './HelpShown';
import { Icon, type IconName } from './Icon';
import { StatusPill } from './StatusPill';
import s from './WaitingOnYou.module.css';

/** decision: needs a choice soon (gold row) · task: role work · getting-started: setup steps. */
export type WaitingKind = 'decision' | 'task' | 'getting-started';

export interface WaitingItem {
  id: string;
  /** The role the item comes from, shown as a tag on the row ("Sector Lead", "Employee"). */
  role: string;
  /** Every role the row comes from when more than one (it then shows one tag for each). */
  roleTags?: readonly string[];
  roleTone?: 'info' | 'neutral';
  kind: WaitingKind;
  title: string;
  detail?: string;
  /** Small line above a decision ("Needs your decision · PAA-2026-0011"). */
  eyebrow?: string;
  /** Due pill ("Due Oct 21 · 7 days left"). */
  due?: string;
  /**
   * A problem with what's asked ("Over budget"): a red pill with these words, and the row's edge
   * and icon in the problem colour. The words carry it, never the colour alone.
   */
  problem?: string;
  /** ISO timestamp of when it started waiting. The list shows the oldest first. */
  since: string;
  action: { label: string; href: string };
  /** Getting-started steps can be dismissed. */
  dismissible?: boolean;
  icon?: IconName;
  /**
   * Which group the row sorts in (APP-054): live work (0, the default), then setup, the more
   * important setup first. A group below 0 comes before live work (an award that's held, APP-148
   * D17). Oldest first within each group.
   */
  group?: number;
  /**
   * How long a row about an award has waited (APP-149 D2): "Waiting since Oct 9 · 5 days". It
   * leads the detail line, semibold. Left out on setup steps and notes.
   */
  waitingSince?: string;
  /** Where the row goes By role (D6): its role's place in the order roles are listed. */
  roleOrder?: number;
  /** Where the row goes By award type (D6); left out, it has none and goes last. */
  awardTypeOrder?: number;
}

/**
 * How Waiting on you is sorted (APP-149 D6): the person's default from My preferences (oldest
 * first, newest first or by role), or by award type, for the visit.
 */
export type WaitingSort = WaitingOrder | 'type';

const SORT_WORDS: Record<WaitingSort, string> = {
  oldest: 'Oldest first',
  newest: 'Newest first',
  role: 'By role',
  type: 'By award type',
};
const SORTS: readonly WaitingSort[] = ['oldest', 'newest', 'role', 'type'];

const isSort = (value: string): value is WaitingSort =>
  (SORTS as readonly string[]).includes(value);

/**
 * The rows in order (D6). Live work is sorted the chosen way; the setup steps and getting-started
 * steps keep their place after it, and a group before it (held awards, APP-148 D17) keeps its
 * place ahead of it, each oldest first, whatever the sort. By role and by award type keep the
 * oldest first in each group; a row with no award type goes last.
 */
export function sortWaiting(items: readonly WaitingItem[], order: WaitingSort): WaitingItem[] {
  const at = (item: WaitingItem): number => Date.parse(item.since);
  const last = Number.MAX_SAFE_INTEGER;
  return [...items].sort((a, b) => {
    const group = (a.group ?? 0) - (b.group ?? 0);
    if (group !== 0) return group;
    if ((a.group ?? 0) === 0) {
      if (order === 'newest') return at(b) - at(a);
      if (order === 'role') {
        const role = (a.roleOrder ?? last) - (b.roleOrder ?? last);
        if (role !== 0) return role;
      }
      if (order === 'type') {
        const type = (a.awardTypeOrder ?? last) - (b.awardTypeOrder ?? last);
        if (type !== 0) return type;
      }
    }
    return at(a) - at(b);
  });
}

/** "Executive, Stock Admin, then Employee": the list's roles, in the order By role puts them. */
function roleWords(items: readonly WaitingItem[]): string {
  const roles = [...items]
    .sort((a, b) => (a.roleOrder ?? 0) - (b.roleOrder ?? 0))
    .map((item) => item.role)
    .filter((role, i, all) => all.indexOf(role) === i);
  if (roles.length <= 1) return roles.join('');
  return `${roles.slice(0, -1).join(', ')}, then ${roles.at(-1) ?? ''}`;
}

/**
 * Something to know, not to do (X1: an Executive Discretionary Bonus the Executives decide
 * outside the app). Shown under the list when it's open; never counted as waiting.
 */
export interface WaitingFyi {
  id: string;
  /** "FYI · EDB-2026-0019". */
  label: string;
  text: string;
  /** Its mark (default: info). */
  icon?: IconName;
  /** One way to find out more ("Preview the calculator", E1). Never a task: nothing is waiting. */
  action?: { label: string; href: string };
}

export interface WaitingOnYouProps {
  /** Everything waiting on the person, across all their roles. */
  items: readonly WaitingItem[];
  /** Notes for information only, under the list ("FYI · EDB-2026-0019 …"). */
  fyi?: readonly WaitingFyi[];
  /** A short line beside Hide ("Also emailed to the Executive group"). */
  note?: string;
  onDismiss?: (id: string) => void;
  help?: CardHelpContent;
  /**
   * Start folded to one line even when items wait (ops Homes, where the queue leads the page).
   * The line keeps the count, a summary and the first decision's action.
   */
  defaultCollapsed?: boolean;
  /**
   * The order the list opens in, from My preferences (APP-149 D7; oldest first by default). The
   * Sort menu changes it while Home is open; Home opens in this order every time (B5).
   */
  defaultOrder?: WaitingOrder;
}

const DEFAULT_HELP: CardHelpContent = {
  shows:
    'Everything that needs you, from every role you hold, in one list. It opens oldest first, or in the order you chose in My preferences. Each row says which role it comes from, and how long an award has waited.',
  canDo:
    'Sort the list for now, open an item to finish it, or dismiss a getting-started step you don’t need. Hide the list and it folds to one line.',
};

/** What Help's "Show me" calls it, on every Home (APP-090). */
const HELP_ANCHOR: HelpAnchorId = 'waiting-on-you';

const DEFAULT_ICON: Record<WaitingKind, IconName> = {
  decision: 'award',
  task: 'clock',
  'getting-started': 'tuning',
};

/** The folded line: "Nothing right now", the setup steps, or the first item and how many more. */
function summary(sorted: readonly WaitingItem[]): string {
  if (sorted.length === 0) return 'Nothing right now';
  if (sorted.every((item) => item.kind === 'getting-started')) {
    return sorted.map((item) => item.title).join(' · ');
  }
  const first = sorted.find((item) => item.kind !== 'getting-started') ?? sorted[0];
  const rest = sorted.length - 1;
  const head = first ? `${first.title}${first.due !== undefined ? ` · ${first.due}` : ''}` : '';
  return rest > 0 ? `${head} · plus ${rest} more` : head;
}

/**
 * The top of every Home. One list across all roles, oldest first, each row tagged with its role.
 * When nothing is waiting it collapses to one line, "Waiting on you · Nothing right now".
 */
export function WaitingOnYou({
  items,
  fyi = [],
  note,
  onDismiss,
  help = DEFAULT_HELP,
  defaultCollapsed = false,
  defaultOrder = 'oldest',
}: WaitingOnYouProps): ReactElement {
  // The person's choice lasts while Home is open (B5); until they make one, their default.
  const [chosen, setChosen] = useState<WaitingSort | null>(null);
  const order = chosen ?? defaultOrder;
  const sorted = useMemo(() => sortWaiting(items, order), [items, order]);
  const count = sorted.length;
  const [choice, setChoice] = useState<boolean | null>(null);
  const open = choice ?? (count > 0 && !defaultCollapsed);
  const decision = sorted.find((item) => item.kind === 'decision');
  const cardId = useId();
  const anchor = useHelpAnchor(HELP_ANCHOR);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // After Hide/Show the button that was pressed is replaced; keep focus on its counterpart.
  const focusToggle = useRef(false);

  useEffect(() => {
    if (!focusToggle.current) return;
    focusToggle.current = false;
    toggleRef.current?.focus();
  }, [open]);

  const toggle = (): void => {
    focusToggle.current = true;
    setChoice(!open);
  };

  if (!open && decision) {
    // Folded with a decision waiting: the line keeps that decision's action next to Show.
    return (
      <section
        className={cx(s.bar, s.barWithAction)}
        aria-labelledby={`${cardId}-title`}
        {...anchor.props}
      >
        <span className={cx(s.barDot, s.dotWaiting)} aria-hidden="true">
          {count}
        </span>
        <h2 id={`${cardId}-title`} className={cx('disp', s.barTitle)}>
          Waiting on you
        </h2>
        <HelpShownTag shown={anchor.shown} />
        <span className={s.barSummary}>
          <span className="visually-hidden">{`${count} waiting: `}</span>
          {summary(sorted)}
        </span>
        <span className={s.barAction}>
          <ButtonLink
            href={decision.action.href}
            variant={decision.roleTone === 'neutral' ? 'gold' : 'navy'}
            size="sm"
          >
            {decision.action.label}
          </ButtonLink>
        </span>
        <AriaButton
          ref={toggleRef}
          className={cx('ui', s.barToggle, s.barToggleButton)}
          aria-expanded={false}
          onPress={toggle}
        >
          Show
          <span className="visually-hidden"> everything waiting on you</span>
          <Icon name="chevronDown" size={16} />
        </AriaButton>
      </section>
    );
  }

  if (!open) {
    // Folded, it's still the region named "Waiting on you" (APP-054), so it's found the same way
    // on every Home; the whole line is the button that shows it.
    return (
      <section className={s.barRegion} aria-labelledby={`${cardId}-title`} {...anchor.props}>
        <HelpShownTag shown={anchor.shown} placement="edge" />
        <AriaButton ref={toggleRef} className={s.bar} aria-expanded={false} onPress={toggle}>
          <span className={cx(s.barDot, count > 0 ? s.dotWaiting : s.dotDone)} aria-hidden="true">
            {count > 0 ? count : <Icon name="check" size={16} />}
          </span>
          <span id={`${cardId}-title`} className={cx('disp', s.barTitle)}>
            Waiting on you
          </span>
          <span className={s.barSummary}>
            <span className="visually-hidden">{count > 0 ? `, ${count} waiting` : ''}</span>·{' '}
            {summary(sorted)}
          </span>
          <span className={cx('ui', s.barToggle)}>
            Show
            <Icon name="chevronDown" size={16} />
          </span>
        </AriaButton>
      </section>
    );
  }

  return (
    <Card
      id={cardId}
      title="Waiting on you"
      helpAnchor={HELP_ANCHOR}
      help={help}
      accent="gold"
      badge={count > 0 ? <StatusPill tone="waiting" label={`${count} waiting`} /> : undefined}
      aside={
        <span className={s.asideRow}>
          {note !== undefined && <span className={s.note}>{note}</span>}
          {count > 1 && (
            <SortMenu
              order={order}
              defaultOrder={defaultOrder}
              roles={roleWords(items)}
              onChange={setChosen}
            />
          )}
          <Button
            ref={toggleRef}
            variant="ghost"
            size="sm"
            aria-expanded={true}
            aria-controls={cardId}
            onPress={toggle}
          >
            Hide
            <Icon name="chevronUp" size={16} />
          </Button>
        </span>
      }
    >
      {count === 0 ? (
        <p className={s.nothing}>
          <span className={s.nothingIcon}>
            <Icon name="check" size={20} strokeWidth={2.4} />
          </span>
          Nothing right now. Anything that needs you shows up here, oldest first.
        </p>
      ) : (
        <ul className={s.list}>
          {sorted.map((item) => (
            <WaitingRow key={item.id} item={item} {...(onDismiss && { onDismiss })} />
          ))}
        </ul>
      )}
      {fyi.length > 0 && (
        <ul className={s.fyiList} aria-label="For your information">
          {fyi.map((entry) => (
            <li key={entry.id} className={s.fyi}>
              <span className={s.fyiIcon} aria-hidden="true">
                <Icon name={entry.icon ?? 'info'} size={18} />
              </span>
              <span className={s.fyiText}>
                <strong>{`${entry.label}.`}</strong> {entry.text}
              </span>
              {entry.action !== undefined && (
                <ButtonLink href={entry.action.href} variant="ghost" size="sm">
                  {entry.action.label}
                </ButtonLink>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * One row of Home's lists: its icon, any pills, the title and the line under it, the role tag and
 * the action. Waiting on others (APP-148, R56) draws its rows with it too.
 */
export function WaitingRow({
  item,
  onDismiss,
}: {
  item: WaitingItem;
  onDismiss?: (id: string) => void;
}): ReactElement {
  const urgent = item.kind === 'decision';
  // APP-248: a part with no words is left out, with its separator.
  const since = item.waitingSince?.trim() ? item.waitingSince : undefined;
  const detail = item.detail?.trim() ? item.detail : undefined;
  const hasTags =
    item.eyebrow !== undefined || item.problem !== undefined || item.due !== undefined;
  return (
    <li
      className={cx(
        s.row,
        urgent && s.urgent,
        item.problem !== undefined && s.problem,
        item.kind === 'getting-started' && s.setup,
      )}
      data-kind={item.kind}
    >
      <span className={s.icon} aria-hidden="true">
        <Icon name={item.icon ?? DEFAULT_ICON[item.kind]} size={urgent ? 26 : 20} />
      </span>
      <div className={s.text}>
        {hasTags && (
          <div className={s.tags}>
            {item.eyebrow !== undefined && (
              <span className={cx('eyebrow', s.eyebrow)}>{item.eyebrow}</span>
            )}
            {item.problem !== undefined && (
              <StatusPill tone="problem" size="sm" label={item.problem} />
            )}
            {item.due !== undefined && <StatusPill tone="waiting" size="sm" label={item.due} />}
          </div>
        )}
        <h3 className={cx(s.title, urgent && item.eyebrow !== undefined && s.titleDisplay)}>
          {item.title}
        </h3>
        {(since !== undefined || detail !== undefined) && (
          <p className={s.detail}>
            {/* APP-149 D2: how long it has waited leads the line. */}
            {since !== undefined && <span className={s.since}>{since}</span>}
            {/* APP-248: the dot is drawn by CSS, so the line's text (what's read, copied or
                found) is "… · today. Hand each one over…", never "… · today · . Hand…"; an
                empty part brings no separator at all. */}
            {since !== undefined && detail !== undefined && (
              <>
                <span className={s.sep} aria-hidden="true" />
                <span className="visually-hidden">. </span>
              </>
            )}
            {detail}
          </p>
        )}
      </div>
      {/* The role tag sits at the right of the row, beside the action, as SYS1 and F1 draw it, so
          titles and tags each line up down the list; on a phone the two wrap below the text
          together (APP-054 decision 23). */}
      <div className={s.actions}>
        {(item.roleTags ?? [item.role]).map((tag) => (
          <StatusPill
            key={tag}
            tone={item.roleTone ?? 'info'}
            size="sm"
            label={tag}
            className={s.role}
          />
        ))}
        <ButtonLink
          href={item.action.href}
          variant={urgent ? 'navy' : 'ghost'}
          size={urgent ? 'md' : 'sm'}
        >
          {item.action.label}
        </ButtonLink>
        {item.dismissible === true && onDismiss && (
          <AriaButton
            className={s.dismiss}
            aria-label={`Dismiss ${item.title}`}
            onPress={() => {
              onDismiss(item.id);
            }}
          >
            <Icon name="close" size={18} />
          </AriaButton>
        )}
      </div>
    </li>
  );
}

/**
 * "Sort: Oldest first" beside Hide (APP-149 D6): the four orders, the person's default marked, and
 * By role naming its order. It sorts the list while Home is open; Home opens at the default.
 */
function SortMenu({
  order,
  defaultOrder,
  roles,
  onChange,
}: {
  order: WaitingSort;
  defaultOrder: WaitingOrder;
  /** "Executive, Stock Admin, then Employee". */
  roles: string;
  onChange: (next: WaitingSort) => void;
}): ReactElement {
  const footId = useId();
  return (
    <MenuTrigger>
      {/* Named by its words alone (label in name, APP-144). */}
      <Button variant="ghost" size="sm">
        Sort: {SORT_WORDS[order]}
        <Icon name="chevronDown" size={16} />
      </Button>
      <Popover className={s.sortPopover} placement="bottom end">
        {/* Named by its button ("Sort: Oldest first"), as every menu in a MenuTrigger is. */}
        <Menu
          className={s.sortMenu}
          aria-describedby={footId}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[order]}
          onSelectionChange={(keys: Selection) => {
            if (keys === 'all') return;
            const [key] = [...keys];
            if (key !== undefined && isSort(String(key))) onChange(String(key) as WaitingSort);
          }}
        >
          {SORTS.map((id) => {
            const sub =
              id === defaultOrder ? 'Your default' : id === 'role' && roles !== '' ? roles : null;
            return (
              <MenuItem key={id} id={id} textValue={SORT_WORDS[id]} className={s.sortOption}>
                {({ isSelected }) => (
                  <>
                    <span className={s.sortTick}>
                      {isSelected && <Icon name="check" size={16} />}
                    </span>
                    <span className={s.sortWords}>
                      <Text slot="label">{SORT_WORDS[id]}</Text>
                      {sub !== null && (
                        <Text slot="description" className={s.sortSub}>
                          {sub}
                        </Text>
                      )}
                    </span>
                  </>
                )}
              </MenuItem>
            );
          })}
        </Menu>
        <p id={footId} className={s.sortFoot}>
          Opens {SORT_WORDS[defaultOrder].toLowerCase()} each time. Change your default in My
          preferences.
        </p>
      </Popover>
    </MenuTrigger>
  );
}
